import React from "react";
import { Row, Col, Form, Button, Card } from "react-bootstrap";
import ConditionRow from "../common/ConditionRow";
import { standardInputStyles, standardLabelStyles } from "../../utils/conditionInputStyles";

export default function WorkflowCriteriaConfig({
  criteriaList,
  onAdd,
  onDelete,
  onChange,
  objectFields,
  readOnly,
  disabled = false,
  eventType = "onCreate", // Add eventType prop - can be string, array, or comma-separated string
}) {
  // Handle multiple event types - if onCreate is in the array, only show "Always"
  // If only onUpdate, show both options. If both, show both options.
  // Support both array format and comma-separated string format
  const eventTypeArray = Array.isArray(eventType)
    ? eventType
    : (typeof eventType === 'string' && eventType.includes(',')
        ? eventType.split(',').map(e => e.trim())
        : [eventType].filter(e => e));
  const hasOnCreate = eventTypeArray.includes("onCreate");
  const hasOnUpdate = eventTypeArray.includes("onUpdate");

  // Custom operators for workflow (using SQL-style operators)
  const operators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
    { value: "<", label: "<" },
    { value: ">", label: ">" },
    { value: "<=", label: "<=" },
    { value: ">=", label: ">=" },
    { value: "IN", label: "IN" },
    { value: "NOT IN", label: "NOT IN" },
    { value: "contains", label: "Contains" },
    { value: "starts_with", label: "Starts With" },
    { value: "ends_with", label: "Ends With" },
    ...(hasOnUpdate ? [{ value: "isChanged", label: "Is Changed" }] : []),
  ];

  const picklistOperators = [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: "is_null", label: "Is Null" },
    { value: "is_not_null", label: "Is Not Null" },
    { value: "IN", label: "IN" },
    { value: "NOT IN", label: "NOT IN" },
  ];

  const evaluateOptions = hasOnCreate && !hasOnUpdate ? ["Always"] : ["Always", "OnChange"];

  return (
    <div className="mt-3">
      {criteriaList != null &&
        criteriaList.length > 0 &&
        criteriaList.map((criteria, index) => (
          <Card key={index} className="mb-3 border">
            <Card.Body className="p-4">
              <Row className="align-items-end">
                {/* Use ConditionRow component for Field-Operator-Value */}
                <Col md={8}>
                  <ConditionRow
                    condition={criteria}
                    fields={objectFields}
                    onChange={onChange}
                    index={index}
                    readOnly={readOnly}
                    options={{
                      fieldKey: 'fieldName',
                      nameKey: 'field_name',
                      labelKey: 'field_label',
                      customOperators: operators,
                      picklistOperators: picklistOperators,
                      fieldColSize: 4,
                      operatorColSize: 3,
                      valueColSize: 5,
                    }}
                  />
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={standardLabelStyles}>Evaluate On</Form.Label>
                    <Form.Select
                      value={eventType === "onCreate" ? "Always" : (criteria.operator === "isChanged" ? "OnChange" : criteria.evaluateOn)}
                      onChange={(e) =>
                        onChange(index, "evaluateOn", e.target.value)
                      }
                      disabled={readOnly || eventType === "onCreate" || criteria.operator === "isChanged"}
                      style={standardInputStyles}
                    >
                      <option value="">-Select-</option>
                      {evaluateOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {!readOnly && (
                  <Col md={2} className="d-flex align-items-end pb-3">
                    <Button
                      variant="outline-danger"
                      onClick={() => onDelete(index)}
                      size="sm"
                      disabled={readOnly}
                      className="w-100"
                    >
                      Delete
                    </Button>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        ))}

      {criteriaList == null || criteriaList.length === 0 ? (
        <div className="text-center py-4 text-muted">
          <p className="mb-0">No criteria configured. Click "Add Criteria" to get started.</p>
        </div>
      ) : null}

      {!readOnly && (
        <Button
          variant="outline-primary"
          onClick={onAdd}
          disabled={readOnly || disabled}
          title={disabled ? "Please select an object first" : ""}
          className="mt-2"
        >
          <i className="fa fa-plus me-2"></i>
          Add Criteria
        </Button>
      )}
    </div>
  );
}
